import type { AlertNotification } from '@thinkalpha/platform-ws-client/contracts/alerts.js';

export type IdeaTriggerMode = 'enter' | 'exit' | 'all';

export interface IdeaAlertRowState {
    enabled: boolean;
    showToast: boolean;
    playSound: boolean;
    soundType: string;
    speakTts: boolean;
    ttsText: string | undefined;
    ttsVoiceUri: string | undefined;
    ttsRate: number;
    ttsPitch: number;
    ttsVolume: number;
    note: string | null;
    triggerMode: IdeaTriggerMode;
    slowIdeaId: string | null;
    fastIdeaId: string | null;
}

export function createInitialIdeaAlertRowState(): IdeaAlertRowState {
    return {
        enabled: false,
        showToast: true,
        playSound: true,
        soundType: 'tambourine',
        speakTts: false,
        ttsText: undefined,
        ttsVoiceUri: undefined,
        ttsRate: 1,
        ttsPitch: 1,
        ttsVolume: 1,
        note: null,
        triggerMode: 'enter',
        slowIdeaId: null,
        fastIdeaId: null,
    };
}

export function stateToNotifications(state: IdeaAlertRowState): AlertNotification[] {
    const notifications: AlertNotification[] = [];
    if (state.showToast) notifications.push({ type: 'toast' });
    if (state.playSound && state.soundType) notifications.push({ type: 'sound', sound: state.soundType });
    if (state.speakTts) {
        notifications.push({
            type: 'tts',
            text: state.ttsText,
            voiceUri: state.ttsVoiceUri,
            rate: state.ttsRate,
            pitch: state.ttsPitch,
            volume: state.ttsVolume,
        });
    }
    return notifications;
}

export function notificationsToState(
    notifications: AlertNotification[],
): Pick<
    IdeaAlertRowState,
    | 'showToast'
    | 'playSound'
    | 'soundType'
    | 'speakTts'
    | 'ttsText'
    | 'ttsVoiceUri'
    | 'ttsRate'
    | 'ttsPitch'
    | 'ttsVolume'
> {
    const showToast = notifications.some((n) => n.type === 'toast');
    const soundNotification = notifications.find((n) => n.type === 'sound');
    const playSound = soundNotification !== undefined;
    const soundType = soundNotification?.type === 'sound' ? soundNotification.sound : 'tambourine';
    const ttsNotification = notifications.find((n) => n.type === 'tts');
    const speakTts = ttsNotification !== undefined;
    const ttsText = ttsNotification?.type === 'tts' ? ttsNotification.text : undefined;
    const ttsVoiceUri = ttsNotification?.type === 'tts' ? ttsNotification.voiceUri : undefined;
    const ttsRate = ttsNotification?.type === 'tts' ? ttsNotification.rate : 1;
    const ttsPitch = ttsNotification?.type === 'tts' ? ttsNotification.pitch : 1;
    const ttsVolume = ttsNotification?.type === 'tts' ? ttsNotification.volume : 1;
    return { showToast, playSound, soundType, speakTts, ttsText, ttsVoiceUri, ttsRate, ttsPitch, ttsVolume };
}
